import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {Object} PriceSavingsRefs
 * @property {HTMLElement} priceContainer
 */

/**
 * A custom element that displays a product's price, compare-at price, and
 * savings pill. Listens for variant select events and swaps in freshly
 * server-rendered markup for the new variant, mirroring how the theme's
 * native <product-price> component keeps itself in sync.
 *
 * @extends {Component<PriceSavingsRefs>}
 */
class PriceSavingsComponent extends Component {
  requiredRefs = ['priceContainer'];

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles the product select event and swaps in the updated price markup.
   * @param {CustomEvent & { target: EventTarget | null, promise: Promise<{ detail: any }> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer } = this.refs;
        const newBlock = html.querySelector(`price-savings-component[data-block-id="${this.dataset.blockId}"]`);
        if (!newBlock) return;

        const newPriceContainer = newBlock.querySelector('[ref="priceContainer"]');
        if (newPriceContainer && priceContainer) {
          priceContainer.replaceWith(newPriceContainer);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[price-savings] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('price-savings-component')) {
  customElements.define('price-savings-component', PriceSavingsComponent);
}
